import { IDropdownOption } from "@fluentui/react/lib/Dropdown";
import {
  useCallback, useContext, useEffect, useState
} from "react";

import { ConfigurationContext } from "Components/Generic/Configuration";
import { TranslationContext } from "Components/Generic/Translator";
import { SearchContext } from "Components/Overview/Search/SearchContext";
import { IFieldMetadata } from "Model/Configuration/IFieldMetadata";
import { EntityType } from "Model/EntityType";
import { isAnyDateField, isDateTimeField } from "Utils/Metadata";
import { isNullOrWhiteSpace } from "Utils/String";
import { getLastSearchTerm } from "Utils/Tokenizr";

import { useDispatch } from "react-redux";
import { projectsFilterExpressionAdd } from "Store/Actions";
import { IUseDatePickerDialogProps } from "./IUseDatePickerDialogProps";
import { DEFAULT_OPERATOR } from "./Operators";

export function useDatePickerDialog(): IUseDatePickerDialogProps {
  const config = useContext(ConfigurationContext);
  const translator = useContext(TranslationContext);
  const { searchTerm, setSearchTerm, setIsCallOutVisible } = useContext(SearchContext);
  const dispatch = useDispatch();
  const [fieldMetadata, setFilterMetadata] = useState<IFieldMetadata>();
  const [isDialogHidden, setIsDatePickerDialogHidden] = useState<boolean>(true);

  useEffect(() => {
    if (isNullOrWhiteSpace(searchTerm) || !searchTerm.endsWith(`:`)) {
      return;
    }

    const { fieldName: potentialFieldName } = getLastSearchTerm(searchTerm);
    const fieldName = config.getFieldNameFromUserInput(potentialFieldName, EntityType.Project);
    if (isNullOrWhiteSpace(fieldName)) {
      return;
    }

    const metadata = config.getMetadata(fieldName, EntityType.Project);
    if (isAnyDateField(metadata)) {
      setFilterMetadata(metadata);
      setIsDatePickerDialogHidden(false);
    }
  }, [config, searchTerm]);

  const [value, setValue] = useState(new Date());
  const [operator, setComparisonOperator] = useState<string>(DEFAULT_OPERATOR.key as string);

  const onConfirm = useCallback(() => {
    if (value == null) {
      return;
    }

    dispatch(projectsFilterExpressionAdd(`${searchTerm}"${operator === `=` ? `` : operator}${value.toISOString()}"`));
    setSearchTerm(``);
    setIsDatePickerDialogHidden(true);
    setIsCallOutVisible(false);
  }, [value, dispatch, searchTerm, operator, setSearchTerm, setIsCallOutVisible]);

  const onDismiss = useCallback(() => {
    setIsDatePickerDialogHidden(true);
  }, []);

  const onChangeOperator = useCallback((_, option: IDropdownOption): void => {
    const { key } = option;
    setComparisonOperator(key as string);
  }, []);

  const onChange = useCallback(
    (newDate: Date) => {
      if (newDate == null) {
        return;
      }

      setValue(newDate);
    },
    [setValue]
  );

  return {
    errorMessage: value == null ? translator.get(`generic.missingvalue`, `This value is missing or invalid.`) : null,
    fieldName: fieldMetadata?.fieldName,
    isDateTime: isDateTimeField(fieldMetadata),
    isDialogHidden,
    onChange,
    onChangeOperator,
    onConfirm,
    onDismiss,
    operator,
    value
  };
}
